// evo/rooms/EvoRoomCard.tsx
import { Button, Text } from '@lmn/design-system';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { css } from 'styled-components';
import { useSearchParams } from 'next/navigation';
import { IconChecking } from '@/app/common/icons/libraryIcons';
import { URLParameters } from '@/app/common/urlParameters';
import { useCreateAsyncPricing } from '@/app/components/hotel-detail/PricingProvider';
import { useFetchHotelStatic } from '@/app/components/hotel-detail/hooks/useFetchHotelStatic';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import { useSelectedMealPlan } from '@/app/components/hotel-detail/SelectedMealPlanProvider';
import { LmnDealBadge } from '@/app/components/hotel-detail/LmnDealBadge';
import FlashSalesBadge from '@/app/components/search/components/EvolutionCard/Gallery/FlashSalesBadge';
import { PeriodicPromotion } from '@/app/common/components/Promotions/LastMinuteDealsBadge/PeriodicPromotion';
import { getActivePeriodicPromotion } from '@/app/common/periodicPromotions';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { RoomBedTypeSelection, shouldShowBedSelection } from '@/app/components/hotel-detail/rooms/RoomBedTypeSelection';
import {
  DebugInfo,
  MealPlanOptionsGroups,
  RoomsIncludedInPrice,
} from '@/app/components/hotel-detail/rooms/RoomPanelCommon';
import { RoomBedOptions } from '@/app/components/hotel-detail/rooms/RoomBedOptions';
import { LocalTaxes } from '@/app/components/hotel-detail/rooms/LocalTaxes';
import { useTranslation } from '@/app/components/translations/Translation';
import {
  getMainAmenities,
  getMealPlanCancellationPolicyLabel,
  getOptionKey,
  hasRoomDetails,
} from '@/app/components/hotel-detail/rooms/roomCommon';
import { hasMealPlanIncludedOptions } from '@/app/components/hotel-detail/rooms/mealPlanIncludedOptions';
import {
  BOARD_CLASS_ICON_INDEX,
  getMealPlanEvoIcon,
} from '@/app/components/search/components/Filters/verticalFilters/Fields/getMealPlanField/getMealPlanField';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import { useHotelStayDates } from '@/app/components/hotel-detail/hooks/useHotelStayDates';
import { SubtleButton } from '@/app/components/hotel-detail/common/button';
import { useRoomPanelOpenModal } from '@/app/components/hotel-detail/rooms/RoomPanelModalProvider';
import { useEvoModals } from '@/app/components/hotel-detail/evo/EvoModalsProvider';
import { useEvoContinueFromRooms } from '@/app/components/hotel-detail/evo/hooks/useEvoContinueFromRooms';
import ImageWithFallback, { DEFAULT_FALLBACK_IMAGE } from '@/app/components/hotel-detail/ImageWithFallback';
import {
  getSingleSource,
  transformationMobileLayout,
} from '@/app/components/hotel-detail/getGallerySourceOptimization';
import { keyDownHandler } from '@/app/components/hotel-detail/common/common.helper';
import { formatPrice } from '@/app/components/search/utils/formatting';
import usePriceDisplay from '@/app/components/hotel-detail/hooks/usePriceDisplay.ts';
import { useRoomAvailabilityAlertLabel } from '@/app/components/hotel-detail/hooks/useRoomAvailabilityAlertLabel';
import { LabelWithIconContainer } from '@/app/components/search/components/Card/Details/Badges/LabelWithIcon/styled';
import {
  getChildrenFreeDescription,
  hasChildrenFreeOffer,
} from '@/app/components/hotel-detail/rooms/childrenFreeOffer';
import PinkPeopleBadge from '@/app/components/search/components/EvolutionCard/Badges/badges/PinkPeopleBadge';
import { EvoRoomAvailabilityAlertLabel } from './EvoRoomAvailabilityAlertLabel';
import { EvoRoomPromotions } from './EvoRoomPromotions';
import { getPromotionGroupKey } from './promotionGroup';
import { EvoRoomInfoLabel, EvoRoomInfoRow } from './EvoRoomInfoRow';
import { EvoKidsStayFreeBadge } from './EvoKidsStayFreeBadge';
import { EvoMyPriceBadge } from './EvoMyPriceBadge';
import { getVisibleEvoRoomBadges } from './evoRoomBadges';
import {
  AmenitiesBlock,
  AmenitiesLine,
  BadgesRow,
  BelowLgCta,
  BelowLgOnly,
  DesktopPrice,
  DebugInfoLink,
  DesktopRoomImage,
  Essentials,
  GalleryCounter,
  GalleryGrid,
  GallerySingle,
  GalleryTopSecret,
  InfoGroup,
  LgUpOnly,
  LowerSection,
  MealPlanTypeRow,
  MobileGallery,
  MobilePrice,
  MobilePriceLine,
  NameLine,
  NameRow,
  PriceRow,
  RoomCardContainer,
  RoomContent,
  RoomDivider,
  SelectedButton,
} from './EvoRoomCard.styled';

const MEAL_PLAN_ICONS = getMealPlanEvoIcon();
const GALLERY_GRID_MIN_IMAGES = 3;

export function EvoRoomCard(props: { room: RoomType; promotionKey: string; selectedBoardClass: string | undefined }) {
  const { t } = useTranslation();
  const initialParams = useInitialParams();
  const { data: asyncPricing } = useCreateAsyncPricing();
  const { data: hotelStaticData } = useFetchHotelStatic();
  const { room, promotionKey, selectedBoardClass } = props;
  const { selectedMealPlan, selectedRoom, setSelectedRoom, setSelectedMealPlan } = useSelectedMealPlan();
  const { localization } = usePageWrapperData();
  const { imageBasePath } = localization;
  // MPH-1322: cux_4123 variant T (DP only) — 18px room name, 8px gap before "Room details", and on
  // mobile a full-width "Select room" CTA under a right-aligned price.
  const ctaChanges = !!localization.configValuesApp?.roomCardCtaChanges;
  const { openModal } = useRoomPanelOpenModal();
  const searchParams = useSearchParams();
  const { totalGuestsWithoutInfants, accommodationOnly } = useUrlSearchParams();
  // Hotel stay nights (not trip nights): the per-night local-taxes math must use the
  // nights actually spent at the hotel, which differ on a DP partial stay.
  const { nights } = useHotelStayDates();
  const { showTotalPrice } = usePriceDisplay();
  const debug = searchParams.get(URLParameters.DEBUG);
  const debugScenario = searchParams.get(URLParameters.DEBUG_SCENARIO);

  // Matched on roomOfferId, not roomId: a room is listed once per rooms-included-in-price count on
  // multi-room searches (same roomId, `${roomId}-${roomsIncludedInPrice}` offer id), so comparing
  // roomId marked both cards as selected (MPH-882).
  const selected =
    room.roomOfferId === selectedRoom?.roomOfferId &&
    selectedMealPlan?.boardClass === selectedBoardClass &&
    (selectedMealPlan?.promotionKey || '') === promotionKey;

  const filteredConfigurations = useMemo(() => {
    const configs = (
      selectedBoardClass
        ? room.configurations.filter((config) => config.boardClass === selectedBoardClass)
        : room.configurations
    ).filter((config) => getPromotionGroupKey(config) === promotionKey);
    return [...configs].sort((a, b) => a.finalPrice - b.finalPrice);
  }, [room.configurations, selectedBoardClass, promotionKey]);

  const cheapestConfiguration: MealPlanType | undefined = filteredConfigurations[0];

  const [currentRoomSelectedMealPlan, setCurrentRoomSelectedMealPlan] = useState<MealPlanType | undefined>(
    cheapestConfiguration
  );

  useEffect(() => {
    setCurrentRoomSelectedMealPlan(
      filteredConfigurations.find((config) => config.id === selectedMealPlan?.id) ?? filteredConfigurations[0]
    );
  }, [filteredConfigurations, selectedMealPlan]);

  const onSelectRoom = () => {
    if (selected) return;
    const target = cheapestConfiguration;
    if (!target) return;
    setCurrentRoomSelectedMealPlan(target);
    setSelectedRoom(room);
    setSelectedMealPlan(target);
  };

  const continueFromRooms = useEvoContinueFromRooms();

  // Mobile/tablet "Continue" (below lg, where there is no trip-overview sidebar): select this room's
  // displayed meal plan and continue to the next step (cancellation-type modal when there are multiple
  // options, otherwise straight to the flight page — or to review on Hotel Only, which has no flights)
  // — mirroring the desktop trip-overview Continue button.
  const onContinue = () => {
    if (!currentRoomSelectedMealPlan) return;
    setSelectedRoom(room);
    setSelectedMealPlan(currentRoomSelectedMealPlan);
    continueFromRooms(room, currentRoomSelectedMealPlan);
  };

  // Price difference is always relative to the meal plan selected on the page, regardless of which board
  // class tab is currently being viewed. A same-price room shows "+ £0" (per design) rather than hiding
  // the price; the display is gated below on having a page-level selectedMealPlan to compare against.
  const priceDiff =
    selectedMealPlan && cheapestConfiguration
      ? showTotalPrice
        ? cheapestConfiguration.finalTotalPrice - selectedMealPlan.finalTotalPrice
        : cheapestConfiguration.finalPrice - selectedMealPlan.finalPrice
      : 0;
  const priceDiffValue = `${priceDiff < 0 ? '-' : '+'} ${formatPrice({ price: Math.abs(priceDiff), localization })}`;
  // MPH-935: showTotalPrice is the price *toggle*, which usePriceDisplay only ever turns on for a
  // PER_PERSON price. Hotel Only is priced TOTAL, so the difference above is already a whole-stay
  // figure and must not be labelled per person — note the key itself is the DP one.
  const perPersonLabel =
    showTotalPrice || accommodationOnly
      ? t('vacation.oss.cards.total')
      : t('vacation.select.room.price.dp.perperson.short');

  const hasImages = room.images.length > 0;
  // MPH-935: a Top Secret response carries no room photos at all — verified against a live TSH hotel,
  // where every room comes back with zero images, amenities and descriptions. The generic
  // "no photo available" placeholder reads as missing content there; the Top Secret mark says the
  // photo is withheld on purpose, which is the whole product.
  const isTopSecret = !!hotelStaticData?.isTopSecret;
  // A Top Secret stay shows no room photo and opens no gallery, whether or not the response happens to
  // carry one: the empty image list is a property of today's payload, not a rule the page can lean on,
  // and the one thing the rate is sold on not telling you must not depend on it.
  const showsPhotos = hasImages && !isTopSecret;
  const fallbackSrc = `${imageBasePath}${DEFAULT_FALLBACK_IMAGE}`;
  const thumbnailSrc = hasImages ? getSingleSource(room.images[0], transformationMobileLayout) : fallbackSrc;
  const galleryImageLabel = showsPhotos ? `${t('vacation.select.rooms.showdetails')} - ${room.name}` : undefined;

  if (!currentRoomSelectedMealPlan) return null;

  const { roomsLeft } = currentRoomSelectedMealPlan;

  return (
    <RoomCardContainer $selected={selected}>
      <MobileGallery
        $clickable={showsPhotos}
        role={showsPhotos ? 'button' : undefined}
        tabIndex={showsPhotos ? 0 : undefined}
        onClick={showsPhotos ? () => openModal(room, currentRoomSelectedMealPlan) : undefined}
        onKeyDown={showsPhotos ? keyDownHandler(() => openModal(room, currentRoomSelectedMealPlan)) : undefined}
        aria-label={galleryImageLabel}
      >
        <EvoRoomCardGallery images={room.images} fallbackSrc={fallbackSrc} isTopSecret={isTopSecret} />
      </MobileGallery>

      <DesktopRoomImage
        $clickable={showsPhotos}
        role={showsPhotos ? 'button' : undefined}
        tabIndex={showsPhotos ? 0 : undefined}
        onClick={showsPhotos ? () => openModal(room, currentRoomSelectedMealPlan) : undefined}
        onKeyDown={showsPhotos ? keyDownHandler(() => openModal(room, currentRoomSelectedMealPlan)) : undefined}
        aria-label={galleryImageLabel}
      >
        {isTopSecret ? <TopSecretRoomImage /> : <ImageWithFallback url={[thumbnailSrc]} alt="" loading="lazy" />}
      </DesktopRoomImage>

      <LowerSection>
        <EvoRoomInfoSummary
          room={room}
          selectedMealPlan={currentRoomSelectedMealPlan}
          roomsLeft={roomsLeft}
          configurations={filteredConfigurations}
          ctaChanges={ctaChanges}
        />

        <RoomDivider />

        <PriceRow $ctaChanges={ctaChanges}>
          {!selected && selectedMealPlan && (
            <>
              <DesktopPrice>
                <Text variant="Title4">{priceDiffValue}</Text>
                <Text variant="BodySmall" weight="bold">
                  {perPersonLabel}
                </Text>
              </DesktopPrice>
              <MobilePrice $ctaChanges={ctaChanges}>
                <MobilePriceLine $ctaChanges={ctaChanges}>
                  <Text variant="Title3">{priceDiffValue}</Text>
                  <Text variant="BodySmall" weight="bold">
                    {perPersonLabel}
                  </Text>
                </MobilePriceLine>
                <LocalTaxes
                  css={
                    ctaChanges
                      ? css`
                          align-items: flex-end;
                          justify-content: flex-end;
                          text-align: right;
                        `
                      : css`
                          align-items: flex-start;
                          justify-content: flex-start;
                        `
                  }
                  nights={nights}
                  passengers={totalGuestsWithoutInfants}
                  roomsIncludedInPrice={room.roomsIncludedInPrice}
                  excludedCharges={currentRoomSelectedMealPlan.excludedCharges}
                  excludedChargesIncluded={currentRoomSelectedMealPlan.excludedChargesIncluded}
                  price={currentRoomSelectedMealPlan.finalTotalPrice}
                  priceWithoutTaxes={currentRoomSelectedMealPlan.priceWithoutTaxes}
                  showTotalPriceIncludedTaxes={true}
                />
              </MobilePrice>
            </>
          )}

          <LgUpOnly>
            {selected ? (
              <SelectedButton
                variant="primary"
                appearance="ghost"
                size="large"
                iconLeading={IconChecking}
                onClick={onSelectRoom}
                aria-pressed={true}
                data-testid="evo-select-room"
              >
                {t('vacation.select.cc.rooms.confirmed')}
              </SelectedButton>
            ) : (
              <Button
                variant="primary"
                appearance="outlined"
                size="large"
                onClick={onSelectRoom}
                data-testid="evo-select-room"
              >
                {t('vacation.select.cc.rooms.select_room')}
              </Button>
            )}
          </LgUpOnly>
          {/* Below lg: every room shows a clickable "Continue" (no "Selected" state) that selects the
              room's meal plan and advances — see onContinue. */}
          <BelowLgOnly>
            <BelowLgCta
              variant="primary"
              appearance="filled"
              size="large"
              onClick={onContinue}
              data-testid="evo-select-room-mobile"
              $ctaChanges={ctaChanges}
            >
              {/* On Hotel Only this CTA is the last step before checkout, so it reads "Book now" — the
                  same key legacy Hotel Only already uses, which keeps the copy identical in both arms of
                  cux_4064. A package still has the flight step to reach, so it keeps "Continue" — except
                  in cux_4123 variant T (MPH-1322, DP only), where it reads "Select room". */}
              {t(
                accommodationOnly
                  ? 'vacation.select.details.book.b'
                  : ctaChanges
                    ? 'vacation.select.cc.rooms.select_room'
                    : 'vacation.select.cc.rooms.continue'
              )}
            </BelowLgCta>
          </BelowLgOnly>
        </PriceRow>
      </LowerSection>

      {debug === 'true' && (
        <DebugInfoLink
          href={`/debug/rooms?pricingId=${asyncPricing?.pricingId}&room=${room.roomId}&mealplan=${currentRoomSelectedMealPlan?.id}${
            debugScenario ? `&${URLParameters.DEBUG_SCENARIO}=${encodeURIComponent(debugScenario)}` : ''
          }`}
          target="_blank"
          rel="noreferrer"
        >
          <DebugInfo>{`${initialParams.internalHotelId} | ${currentRoomSelectedMealPlan?.supplier.id} | RoomId: ${room.roomId} - ${currentRoomSelectedMealPlan?.supplier.roomId}`}</DebugInfo>
        </DebugInfoLink>
      )}
    </RoomCardContainer>
  );
}

/**
 * MPH-935: what a room card shows instead of a photo on a Top Secret stay, where the response carries
 * none by design. Matches the legacy page: the Top Secret mark centred on a tinted panel, rather than
 * the generic "no photo available" artwork, which would read as content that failed to load.
 */
function TopSecretRoomImage() {
  const { localization } = usePageWrapperData();
  return (
    <GalleryTopSecret>
      {/* Decorative on purpose: the mark stands in for a photo rather than conveying anything the
          card does not already say, and the Top Secret terms explain the product once per page. */}
      <img src={`${localization.imageBasePath}logo-image-tsh.svg`} alt="" />
    </GalleryTopSecret>
  );
}

// Mobile-only image preview: a 1+2 grid when there are at least 3 images, otherwise a single
// image. A photo counter is shown bottom-right whenever there is more than one image.
function EvoRoomCardGallery({
  images,
  fallbackSrc,
  isTopSecret,
}: {
  images: readonly string[];
  fallbackSrc: string;
  isTopSecret: boolean;
}) {
  const total = images.length;
  const showCounter = total > 1;

  // A Top Secret stay: the panel stands in for the photos, whether or not any arrived.
  if (isTopSecret) return <TopSecretRoomImage />;

  const counter = showCounter ? (
    <GalleryCounter role="img" aria-label={`${total} photos`}>
      <Text variant="BodySmall" color="textOnDark" forceTag="span" aria-hidden="true">
        {total}
      </Text>
    </GalleryCounter>
  ) : null;

  if (total >= GALLERY_GRID_MIN_IMAGES) {
    return (
      <>
        <GalleryGrid>
          {images.slice(0, 3).map((image) => (
            <ImageWithFallback
              key={image}
              url={[getSingleSource(image, transformationMobileLayout)]}
              alt=""
              loading="lazy"
            />
          ))}
        </GalleryGrid>
        {counter}
      </>
    );
  }

  return (
    <>
      <GallerySingle>
        <ImageWithFallback
          url={[total > 0 ? getSingleSource(images[0], transformationMobileLayout) : fallbackSrc]}
          alt=""
          loading="lazy"
        />
      </GallerySingle>
      {counter}
    </>
  );
}

function EvoRoomInfoSummary(props: {
  room: RoomType;
  selectedMealPlan: MealPlanType;
  roomsLeft: number;
  configurations: MealPlanType[];
  ctaChanges: boolean;
}) {
  const { room, selectedMealPlan, roomsLeft, configurations, ctaChanges } = props;

  const { t } = useTranslation();
  const { setSelectedMealPlan } = useSelectedMealPlan();
  const { openModal } = useRoomPanelOpenModal();
  const { openCancellationPoliciesModal, openMealPlanInfoModal } = useEvoModals();
  const { localization, commonData } = usePageWrapperData();
  const { locale } = localization;
  const urlSearchParams = useUrlSearchParams();
  const { accommodationOnly } = urlSearchParams;

  const mealPlanIcon = MEAL_PLAN_ICONS[BOARD_CLASS_ICON_INDEX[selectedMealPlan.boardClass]];
  const mealPlanLabel = t(getOptionKey(selectedMealPlan.boardClass, accommodationOnly));
  // MPH-946: the info icon lists the meals/drinks the plan includes — only when there is something
  // with copy to list (room only and unmapped board codes come with no options).
  const showMealPlanInfo = hasMealPlanIncludedOptions(selectedMealPlan);
  const mealPlanInfoIcon = { category: 'Semantics', name: 'icon-circle-info', color: 'iconSecondary' } as const;

  // Single inline "·"-separated line: size + room amenities (occupancy / bathroom count live elsewhere).
  const amenities = getMainAmenities(t, room).filter(
    (amenity) => amenity.type !== 'MAX_OCCUPANCY' && amenity.type !== 'BATH_QUANTITY' && !!amenity.description
  );

  const hasDetails = hasRoomDetails(room);

  const { show: showAvailability } = useRoomAvailabilityAlertLabel(roomsLeft);

  const hasFlashSalePromotion = selectedMealPlan.promotions?.some(
    (promotion) => promotion.type?.toUpperCase() === 'FLASH_SALE'
  );
  // MPH-744: FLASH_SALE rates only carry the lastminute-deals branding on brands with the
  // lastMinuteDeals config; other brands show the generic flash-sales badge — the same
  // FlashSalesBadge the results-page card gallery renders (ContainerFlashSales when the
  // config is off).
  const lastMinuteDeals = !!localization.configValuesApp?.lastMinuteDeals;
  // The periodic-promotion badge (Black Friday, Cyber Monday, …) rides on flash-sale rates and shows
  // alongside the LMN deal badge, budget permitting. getActivePeriodicPromotion is the same
  // date/feature-flag logic PeriodicPromotion uses internally, so both always agree on which
  // promotion (if any) is active.
  const activePeriodicPromotion = getActivePeriodicPromotion(commonData.serverTime, localization);
  const visibleBadges = getVisibleEvoRoomBadges({
    MY_PRICE: !!selectedMealPlan.isMyPrice,
    PINK_PEOPLE: !!selectedMealPlan.isEmployeeDiscount,
    LMN_DEAL: !!hasFlashSalePromotion,
    KIDS_STAY_FREE: hasChildrenFreeOffer(configurations),
    PERIODIC_PROMOTION: !!hasFlashSalePromotion && !!activePeriodicPromotion,
    ROOMS_LEFT: showAvailability,
  });
  const childrenFreeDescription = getChildrenFreeDescription(configurations);

  const isRefundable = selectedMealPlan.cancellationPolicy.type === 'REFUNDABLE';
  const cancellationLabel = getMealPlanCancellationPolicyLabel(selectedMealPlan, accommodationOnly, t, locale);
  // Refundable rates use the green "success" shield + info icons and a green label.
  const cancellationIcon = isRefundable
    ? ({ category: 'Security and safety', name: 'icon-shield', color: 'iconSuccess' } as const)
    : ({ category: 'Security and safety', name: 'icon-slashed-shield', color: 'iconPrimary' } as const);
  const cancellationInfoIcon = isRefundable
    ? ({ category: 'Semantics', name: 'icon-circle-info', color: 'iconSuccess' } as const)
    : ({ category: 'Semantics', name: 'icon-circle-info', color: 'iconSecondary' } as const);

  const onSelectMealPlan = (mealPlan: MealPlanType) => {
    setSelectedMealPlan(mealPlan);
  };

  return (
    <RoomContent>
      <NameRow>
        <NameLine>
          {/* MPH-1322: Title4 is 18px but defaults to <h4>; keep the h2 → h3 heading order. */}
          <Text variant={ctaChanges ? 'Title4' : 'Title3'} forceTag="h3">
            {room.name}
          </Text>
          {room.roomsIncludedInPrice > 1 && <RoomsIncludedInPrice roomsIncludedInPrice={room.roomsIncludedInPrice} />}
        </NameLine>
        {visibleBadges.length > 0 && (
          <BadgesRow>
            {visibleBadges.includes('MY_PRICE') && (
              <EvoMyPriceBadge discountPercentage={selectedMealPlan.discountPercentage} />
            )}
            {/* The results card's own badge, reused rather than restyled so the members-only rate
                reads the same wherever it appears — the way PeriodicPromotion below is reused. */}
            {visibleBadges.includes('PINK_PEOPLE') && <PinkPeopleBadge />}
            {visibleBadges.includes('LMN_DEAL') &&
              (lastMinuteDeals ? <LmnDealBadge showInfo={false} /> : <FlashSalesBadge />)}
            {visibleBadges.includes('KIDS_STAY_FREE') && <EvoKidsStayFreeBadge description={childrenFreeDescription} />}
            {visibleBadges.includes('PERIODIC_PROMOTION') && (
              <div
                css={css`
                  display: contents;

                  /* PeriodicPromotion renders a search-card LabelWithIcon with a default margin-bottom;
                drop it so the badge aligns within BadgesRow, which already spaces items via gap. */
                  ${LabelWithIconContainer} {
                    margin-bottom: 0;
                  }
                `}
              >
                <PeriodicPromotion />
              </div>
            )}
            {visibleBadges.includes('ROOMS_LEFT') && <EvoRoomAvailabilityAlertLabel roomsLeft={roomsLeft} />}
          </BadgesRow>
        )}
      </NameRow>

      <InfoGroup>
        <AmenitiesBlock $ctaChanges={ctaChanges}>
          {amenities.length > 0 && (
            <AmenitiesLine>
              {amenities.map((amenity, index) => (
                <Fragment key={`${amenity.type}-${amenity.description}`}>
                  {index > 0 && (
                    <Text variant="Body" color="textDisabled" forceTag="span">
                      ·
                    </Text>
                  )}
                  <Text variant="Body" color="textSecondary" forceTag="span">
                    {amenity.description}
                  </Text>
                </Fragment>
              ))}
            </AmenitiesLine>
          )}

          {hasDetails && (
            <SubtleButton
              onClick={() => openModal(room, selectedMealPlan)}
              data-testid="show-room-details"
              css={css`
                color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
              `}
            >
              <Text variant="Body">{t('vacation.select.cc.rooms.see_room_info')}</Text>
            </SubtleButton>
          )}
        </AmenitiesBlock>

        <Essentials>
          {room.bedOptions?.length ? (
            shouldShowBedSelection(selectedMealPlan.bedId, room.bedOptions) ? (
              <MealPlanOptionsGroups $hideBorder={false}>
                {/* Scope the bed selector to this card's (board × promotion) group: its comparator
                    does not filter by promotionKey, so with the full room it could jump to a rate
                    belonging to another card (MPH-652). */}
                <RoomBedTypeSelection
                  context="desktop-header"
                  room={{ ...room, configurations }}
                  selectedMealPlan={selectedMealPlan}
                  onSelectMealPlan={onSelectMealPlan}
                />
              </MealPlanOptionsGroups>
            ) : (
              <RoomBedOptions bedOptions={room.bedOptions} iconSize={20} fontSize="Body" />
            )
          ) : null}

          <MealPlanTypeRow>
            {mealPlanIcon}
            <EvoRoomInfoLabel
              label={mealPlanLabel}
              color="textPrimary"
              infoIcon={showMealPlanInfo ? mealPlanInfoIcon : undefined}
              onInfoClick={(event) => {
                event.stopPropagation();
                openMealPlanInfoModal(selectedMealPlan.id);
              }}
            />
          </MealPlanTypeRow>

          <EvoRoomInfoRow
            icon={cancellationIcon}
            label={cancellationLabel}
            color={isRefundable ? 'textSuccess' : 'textPrimary'}
            infoIcon={cancellationInfoIcon}
            onInfoClick={(event) => {
              event.stopPropagation();
              openCancellationPoliciesModal(room);
            }}
          />

          <EvoRoomPromotions configurations={configurations} />
        </Essentials>
      </InfoGroup>
    </RoomContent>
  );
}
